import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RestaurantProvider } from "./contexts/RestaurantContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { DeviceAutoLogin } from "./components/DeviceAutoLogin";
import { RoleGuard } from "./components/RoleGuard";
import Login from "./pages/Login";

// Lazy-loaded routes — keeps initial JS bundle small (LCP/FCP optimization).
// Login stays eager because it's the entry/LCP page for unauthenticated visits.
const Index = lazy(() => import("./pages/Index"));
const KitchenView = lazy(() => import("./pages/KitchenView"));
const ServiceView = lazy(() => import("./pages/ServiceView"));
const PrepList = lazy(() => import("./pages/PrepList"));
const OrderAssist = lazy(() => import("./pages/OrderAssist"));
const MenuManagement = lazy(() => import("./pages/MenuManagement"));
const SnoozedView = lazy(() => import("./pages/SnoozedView"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminRestaurantDetail = lazy(() => import("./pages/AdminRestaurantDetail"));
const AdminMenuHandoff = lazy(() => import("./pages/AdminMenuHandoff"));
const Information = lazy(() => import("./pages/Information"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <DeviceAutoLogin>
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><RoleGuard><RestaurantProvider><Index /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="/kitchen" element={<ProtectedRoute><RoleGuard><RestaurantProvider><KitchenView /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="/service" element={<ProtectedRoute><RoleGuard><RestaurantProvider><ServiceView /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="/prep" element={<ProtectedRoute><RoleGuard><RestaurantProvider><PrepList /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="/order-assist" element={<ProtectedRoute><RoleGuard><RestaurantProvider><OrderAssist /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="/menu" element={<ProtectedRoute><RoleGuard><RestaurantProvider><MenuManagement /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="/snoozed" element={<ProtectedRoute><RoleGuard><RestaurantProvider><SnoozedView /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="/information" element={<ProtectedRoute><RoleGuard><Information /></RoleGuard></ProtectedRoute>} />
          <Route path="/contact" element={<ProtectedRoute><RoleGuard><RestaurantProvider><Contact /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><RoleGuard><AdminPanel /></RoleGuard></ProtectedRoute>} />
          <Route path="/admin/restaurants/:id" element={<ProtectedRoute><RoleGuard><AdminRestaurantDetail /></RoleGuard></ProtectedRoute>} />
          <Route path="/admin/restaurants/:id/open-menu" element={<ProtectedRoute><RoleGuard><RestaurantProvider><AdminMenuHandoff /></RestaurantProvider></RoleGuard></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </DeviceAutoLogin>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;